/**
 * The two renderings `/studio` needs that `useFormat()` does not have.
 *
 * Both are here rather than in `format/useFormat.ts` because that file is not this
 * item's to edit (session 20, item A). Both belong there, and the report asks for
 * them:
 *
 *   - **A fraction as a percentage** is `format/percent.ts`'s now. It was here and
 *     also in `pages/studio/clients/percent.ts`, with the same two digit constants
 *     under two names and the arguments in the other order.
 *   - **A month, short.** `format.month` is `Aug 2026`; an axis with eight of them
 *     on it wants `Aug`. `PeriodSeg` splits the same string for the same reason.
 *
 * The bar geometry that used to be here — a fraction as points of a hundred — is
 * `components/portal/Meter.tsx`'s now, beside the component it is about.
 *
 * Neither invents a figure: the read model decided the share and the period, and
 * these choose how many characters of it to print.
 */

import type { Formatter } from '../../../format/useFormat.js'

/**
 * How every amount on this page is printed: `$9,750`, never `$9,750.00`.
 *
 * `analytics.html` prints whole amounts throughout and `formatMoney`'s default is
 * `showCents: 'always'`, so the option is on every `format.money` call here. One
 * constant, because eight files each declaring it is eight places for the page to
 * start disagreeing with itself about cents.
 */
export const WHOLE_AMOUNT = { showCents: 'auto' } as const

/** `2026-08` becomes `Aug`. An axis label; the year is already in the section head. */
export function shortMonth(period: string, format: Formatter): string {
  return format.month(period).split(' ')[0] ?? period
}
